'use client';

import { visitorTrafficType } from './visitor-journey';
import posthog from 'posthog-js';

type ProductEvent =
  | 'tracking_details_opened' | 'schedule_quote_started'
  | 'feature_viewed' | 'feature_engaged' | 'content_depth_reached' | 'feature_link_clicked' | 'page_visit_ended'
  | 'result_feedback' | 'result_feedback_reason'
  | 'schedule_search_started' | 'schedule_search_failed' | 'schedule_results_viewed'
  | 'widget_loaded'
  | 'tracking_search_started'
  | 'tracking_search_failed'
  | 'tracking_result_viewed'
  | 'shipment_save_signup_prompted'
  | 'shipment_save_limit_reached'
  | 'shipment_saved'
  | 'sign_up'
  | 'profile_claimed'
  | 'account_login'
  | 'account_login_failed'
  | 'confirmation_email_resent'
  | 'begin_checkout'
  | 'purchase';

// Never send references, customer names, notes, routes or raw API errors.
type ProductProperties = {
  page_area?: string;
  destination_area?: string;
  placement?: 'navigation' | 'footer' | 'content';
  /** Fixed data-cta id of the clicked link (code-defined, never user text). */
  cta?: string;
  visible_time_bucket?: string;
  scroll_percent?: number;
  outcome?: string;
  helpful?: 'yes' | 'no';
  feedback_reason?: string;
  results_count?: number;
  duration_ms?: number;
  surface?: 'site' | 'widget';
  partner_slug?: string;
  result_kind?: 'live' | 'archive' | 'paywall' | 'empty' | 'unavailable';
  http_status?: number;
  limit?: number;
  save_source?: 'manual' | 'after_signup';
  account_type?: 'shipper' | 'forwarder';
  method?: 'email';
  transaction_id?: string;
  value?: number;
  currency?: string;
};

export function recordProductEvent(name: ProductEvent, properties: ProductProperties = {}, destinations: { posthog?: boolean } = {}) {
  if (typeof window === 'undefined' || process.env.NODE_ENV !== 'production') return;
  const trafficType = visitorTrafficType();
  const payload = { ...properties, ...(trafficType ? { traffic_type: trafficType } : {}) };
  // Analytics must never turn a successful product action into a UI failure.
  try { if (destinations.posthog !== false) posthog.capture(name, payload); } catch { /* best effort */ }
  try { window.gtag?.('event', name, payload); } catch { /* best effort */ }
}

export function identifyProductUser(id: string, accountType: 'shipper' | 'forwarder') {
  try { posthog.identify(id, { account_type: accountType }); } catch { /* best effort */ }
}

/** Call only with the response from the authenticated checkout verification API. */
export function recordVerifiedPurchase(data: { accountType: 'shipper' | 'forwarder'; payment?: {
  transaction_id: string; value: number; currency: string; live: boolean;
} | null }) {
  const payment = data.payment;
  if (!payment?.live || !payment.transaction_id || payment.value <= 0) return;
  const key = `shipzy-purchase:${payment.transaction_id}`;
  try { if (sessionStorage.getItem(key)) return; } catch { /* storage may be disabled */ }
  // Stripe's invoice.paid webhook owns PostHog payment_confirmed. This browser
  // event fills GA4's purchase step without duplicating the server event.
  recordProductEvent('purchase', { transaction_id: payment.transaction_id, value: payment.value,
    currency: payment.currency, account_type: data.accountType }, { posthog: false });
  try { sessionStorage.setItem(key, '1'); } catch { /* best effort */ }
}
